import React, { useState, useEffect, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import Filter from 'src/components/common/Filter';
import LoadingSpinner from 'src/components/common/LoadingSpinner';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { IRootState } from 'src/reducers';
import Page from 'src/components/common/Page';
import Panel from 'src/components/common/Panel';
import { useFetch } from 'use-http';
import { ITableConfig } from 'src/components/common/table';
import LeaseRecordingsList from './LeaseRecordingsList';
import LeaseRecording from 'src/models/dlcLease/LeaseRecording';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import { ODataOptions } from 'src/util/ODataBuilder';
import Tooltip from 'react-tooltip-portal';

const LeaseRecordingsPage = () => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const { response, get } = useFetch();
    const dialogContext = useContext<dialogCreators>(DialogContext);
    
    const parentListTableConfig: ITableConfig = {
        rowCount: 15,
        pageNumber: 1,
        idField: 'id',
        columns: [{ name: 'Number', mapping: 'id' }],
    };

    // TODO: Look into setting up a ts file containing tool tips (similar to DialogName enum)
    const leaseRecTooltip = 'Select Leases from list below to add recording information. Select Assignments or Transmittals buttons to batch recorded Leases.';

    // const defaultBatchOptions: { exercise: boolean; recording: boolean; assign: boolean } = {
    //     exercise: true,
    //     recording: true,
    //     assign: true,
    // };

    // const [generatedContracts, setGeneratedContracts] = useState<PagedResultSet<ContractGenerationListDto>>(null);
    const [leaseRecordingsList, setLeaseRecordingsList] = useState<PagedResultSet<LeaseRecording>>(null);
    // const [cPageNumber, setCPageNumber] = useState<number>(0);
    const [lrPageNumber, setLrPageNumber] = useState<number>(0);
    // const [cFilterOptions, setCFilterOptions] = useState<ODataOptions>(ODataOptions.from(parentListTableConfig));
    const [lrFilterOptions, setLrFilterOptions] = useState<ODataOptions>(ODataOptions.from(parentListTableConfig));
    // const [selectedContracts, setSelectedContracts] = useState<Array<number>>([]);
    // const [batchOptions, setBatchOptions] = useState(defaultBatchOptions);

    // Tooltip states
    const leaseRecordRef = useRef(null);
    const [showLRTip, setShowLRTip] = useState<boolean>(false);

    // useEffect(() => {
    //     if (cFilterOptions.hasFilters()) {
    //         getGeneratedContracts();
    //     }
    // }, [cFilterOptions]);

    useEffect(() => {
        getLeaseRecordings();
    }, [lrFilterOptions]);
    
    // const initFilterOptions = () => {
    //     let newOptions = ODataOptions.from(agreementsPageTableConfig);
    //     newOptions.addFilter(new ODataFilter('where', ODataFilterOperators.Equals, 'isInHand,1'));
    //     setFilterOptions(newOptions.toString());     
    // };

    // const onIncrementCPage = () => {
    //     if (cPageNumber * generatedContracts.pageSize < generatedContracts.totalRecords) {
    //         let pConfig = parentListTableConfig;
    //         pConfig.pageNumber = cPageNumber + 1;
            
    //         let initOptions = ODataOptions.from(pConfig);
            
    //         if (initOptions.getOrderByProperty() === undefined) {
    //             initOptions.addOrderBy('leaseNumber', ODataOrderByDirection.Descending);
    //         }
    //         initOptions.addFilter(new ODataFilter('where', ODataFilterOperators.Equals, 'isInHand,1'));
    //         initOptions.merge(cFilterOptions);
    //         setCFilterOptions(initOptions);
    //         setCPageNumber(pConfig.pageNumber);
    //     }
    // };

    const onIncrementLrPage = () => {
        if (lrPageNumber * leaseRecordingsList.pageSize < leaseRecordingsList.totalRecords) {
            let pConfig = parentListTableConfig;
            pConfig.pageNumber = lrPageNumber + 1;
            
            let initOptions = ODataOptions.from(pConfig);
            
            initOptions.merge(lrFilterOptions);
            setLrFilterOptions(initOptions);
            setLrPageNumber(pConfig.pageNumber);
        }
    };

    // const getGeneratedContracts = async () => {
    //     // Get contracts and assign to generatedContracts.
    //     let result = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/leases/paged/${cFilterOptions.toString()}`);

    //     if (response.ok) {
    //         if (cPageNumber > 1) {
    //             let contracts = generatedContracts.items.concat(result.items);
    //             result.items = contracts;
    //         }
    //         setGeneratedContracts(result);
    //     } else {
    //         console.log('getGeneratedContracts Error:', result);
    //         window.alert('Error getting Contract Data');
    //     }
    // };

    const getLeaseRecordings = async () => {
        let result = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/processingMaintenance/lease-recording${lrFilterOptions.toString()}`);

        if (response.ok) {
            if (lrPageNumber > 1) {
                let records = leaseRecordingsList.items.concat(result.items);
                result.items = records;
            }
            setLeaseRecordingsList(result);
        } else {
            console.log('getLeaseRecordings Error:', result);
            window.alert('Error getting Lease Recordings');
        }
    };
    
    const onUpdateLeaseRecording = (leaseNumber: number, id?: number) => {
        let recording = id > 0 ? leaseRecordingsList?.items?.find(r => r.lr_tg === id) : undefined;

        dialogContext.processing.recordLease(leaseNumber, recording, () => onUpdate());
    };

    const onUpdate = () => {
        getLeaseRecordings();
    };

    // const handleCFilterChanged = (options: ODataOptions) => {
    //     setCPageNumber(1);
    //     let initOptions = ODataOptions.from(parentListTableConfig);

    //     if (options.getOrderByProperty() === undefined) {
    //         options.addOrderBy('leaseNumber', ODataOrderByDirection.Descending);
    //     }
    //     options.addFilter(new ODataFilter('where', ODataFilterOperators.Equals, 'isInHand,1'));
    //     initOptions.merge(options);
    //     setCFilterOptions(initOptions);
    // };

    const handleLrFilterChanged = (options: ODataOptions) => {
        setLrPageNumber(1);
        let initOptions = ODataOptions.from(parentListTableConfig);

        initOptions.merge(options);
        setLrFilterOptions(initOptions);
    };

    const leaseRecordingsWindow = () => {
        return (
            <Panel>
                <div>
                    <div className="dlc-content-panel-header">
                        <span>Leases for Recording</span>
                        <span
                            className="tool-tip"
                            ref={leaseRecordRef}
                            onMouseEnter={() => setShowLRTip(true)}
                            onMouseLeave={() => setShowLRTip(false)}
                        >
                            <i className="fa fa-info-circle" />
                        </span>
                        <Tooltip active={showLRTip} parent={leaseRecordRef.current} position="top" className="tooltip">
                            <div>{leaseRecTooltip}</div>
                        </Tooltip>
                    </div>
                    <Filter
                        sortItems={[
                            { group: 1, text: 'Lease No', icon: 'alpha-down', orderName: 'lm.LS_NO', orderDirection: 'descending' },
                            { group: 1, text: 'Invoice No', icon: 'alpha-down', orderName: 'li.INVOICE_NO1', orderDirection: 'descending' },
                            { group: 1, text: 'Assign No', icon: 'alpha-down', orderName: 'lr.ASSIGN_NO', orderDirection: 'descending' },
                            { group: 1, text: 'Lessor Name', icon: 'alpha-down', orderName: 'll.NAME1', orderDirection: 'descending' },
                        ]}
                        filterItems={[
                            { group: 1, text: 'Lease No', orderName: 'lease_no', orderDirection: 'descending' },
                            { group: 1, text: 'Invoice No', orderName: 'invoice_no', orderDirection: 'descending' },
                            { group: 1, text: 'Assign No', orderName: 'assign_no', orderDirection: 'descending' },
                            { group: 1, text: 'Lessor Name', orderName: 'lessor_name', orderDirection: 'descending' },
                        ]}
                        onFilterChanged={handleLrFilterChanged}
                        shortWidth={true}
                    />
                </div>
                <div style={{ overflow: 'auto', height: '74vh' }}>
                    {!!leaseRecordingsList && (
                        <LeaseRecordingsList
                            leaseRecordings={leaseRecordingsList}
                            forBatching={false}
                            isAssigned={false}
                            selectList={[]}
                            onUpdateLeaseRecording={onUpdateLeaseRecording}
                            incrementPage={onIncrementLrPage}
                        />
                    )}
                    {!leaseRecordingsList && <LoadingSpinner />}
                </div>
            </Panel>
        );
    };

    // const generatedContractsPanel = () => {
    //     return (
    //         <Panel>
    //             <div>
    //                 <div className="dlc-content-panel-header">Active Contracts</div>
    //                 <Filter
    //                     sortItems={[
    //                         { group: 1, text: 'Contract Type', icon: 'alpha-up', orderName: 'leaseType', orderDirection: 'ascending' },
    //                         { group: 1, text: 'Contract Number', icon: 'alpha-up', orderName: 'leaseNumber', orderDirection: 'ascending' },
    //                         { group: 1, text: 'Owner Name', icon: 'alpha-up', orderName: 'ownerName', orderDirection: 'ascending' },
    //                         { group: 1, text: 'Leased By', icon: 'numeric-up', orderName: 'leasedBy', orderDirection: 'ascending' },
    //                     ]}
    //                     filterItems={[
    //                         { group: 1, text: 'Contract Type', orderName: 'leaseType', orderDirection: 'descending' },
    //                         { group: 1, text: 'Contract Number', orderName: 'leaseNumber', orderDirection: 'descending' },
    //                         { group: 1, text: 'Owner Name', orderName: 'ownerName', orderDirection: 'descending' },
    //                         { group: 1, text: 'Leased By', orderName: 'leasedBy', orderDirection: 'descending' },
    //                     ]}
    //                     onFilterChanged={handleCFilterChanged}
    //                 />
    //             </div>
    //             <div style={{ overflow: 'auto', height: '65vh' }}>
    //                 {!!generatedContracts && (
    //                     <AgreementsList
    //                         generatedAgreements={generatedContracts}
    //                         pageType="Processing&Maintenance"
    //                         clientNumber={appContext.clientNumber}
    //                         projectNumber={appContext.projectNumber}
    //                         selectedContracts={selectedContracts}
    //                         incrementPage={onIncrementCPage}
    //                         setSelectedContracts={setSelectedContracts}
    //                         onUpdateLeaseRecording={onUpdateLeaseRecording}
    //                     />
    //                 )}
    //                 {!generatedContracts && <LoadingSpinner />}
    //             </div>
    //         </Panel>
    //     );
    // };

    return (
        <Page>
            {/* <Page.Section width={7}>
                {generatedContractsPanel()}
            </Page.Section> */}
            <Page.Section>
                {leaseRecordingsWindow()}
            </Page.Section>
        </Page>
    );
};

export default LeaseRecordingsPage;


// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/ProcessingAndMaintenance/LeaseRecordingsPage.tsx